import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSpecFormat } from '../../../hooks/useSpecRequirements.js';

export interface FormatHintProps {
  /** 成果物の種類（business_flow / features / narrative） */
  artifact: string;
  /** 見出しに出す名前 */
  label: string;
}

/**
 * 書き方のルールをその場で開く。
 *
 * **編集している場所の隣に置く。** 別画面やドキュメントに置くと、書いている途中で見に行く
 * 人がいなくなり、保存してから弾かれて初めて読むことになる。
 *
 * 中身は API がフォーマット文書をそのまま返す（画面にルールを写さない）。開くまで取りに
 * 行かないので、編集しない人には読み込みが走らない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物の書き方（必須要素・省く要素・サンプル）を編集中にその場で開く
 * @graph-connects /spec-requirements [calls] フォーマット文書の取得
 */
export function FormatHint({ artifact, label }: FormatHintProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const format = useSpecFormat(artifact, open);

  return (
    <div className="mt-3 border border-[var(--color-border)] rounded bg-[var(--color-bg-elevated)]">
      <button
        type="button"
        className="text-xs text-[var(--color-text-secondary)] px-3 py-2 w-full text-left cursor-pointer disabled:cursor-not-allowed"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {t(open ? 'spec.formatHint.close' : 'spec.formatHint.open', { label })}
      </button>
      {open ? (
        <div className="px-3 pb-3">
          {format.error !== null ? (
            <p className="text-sm text-[var(--color-error-text)]">
              {t('spec.formatHint.loadFailed')}
            </p>
          ) : format.data === undefined ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('spec.loading')}</p>
          ) : (
            <pre className="text-xs whitespace-pre-wrap font-mono text-[var(--color-text-primary)] overflow-x-auto">
              {format.data.markdown}
            </pre>
          )}
        </div>
      ) : null}
    </div>
  );
}
